import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Fab
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getApiUrl } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import PostCard from '../components/Posts/PostCard';

const Home = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const fetchPosts = async (pageNum = 1, reset = false) => {
    try {
      setLoading(true);
      const response = await axios.get(getApiUrl(`/api/posts?page=${pageNum}&limit=10`));
      
      if (reset) {
        setPosts(response.data.posts);
      } else {
        setPosts(prev => [...prev, ...response.data.posts]);
      }
      
      setHasMore(response.data.pagination.hasNext);
      setError('');
    } catch (err) {
      console.error('Error fetching posts:', err);
      setError('Failed to load posts. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts(1, true);
  }, []);

  const handleLoadMore = () => {
    if (!loading && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchPosts(nextPage);
    }
  };

  const handlePostUpdate = (updatedPost) => {
    setPosts(prev => prev.map(post => 
      post._id === updatedPost._id ? updatedPost : post
    ));
  };

  if (loading && posts.length === 0) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, pb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Discover Amazing Trips
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Share your travel experiences and explore the world through others' journeys
        </Typography>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Posts Feed */}
      <Grid container spacing={3}>
        {posts.length === 0 && !loading ? (
          <Grid item xs={12}>
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No posts yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isAuthenticated 
                  ? "Be the first to share your trip experience!"
                  : "Join TripShare to start sharing your adventures!"
                }
              </Typography>
            </Box>
          </Grid>
        ) : (
          posts.map((post) => (
            <Grid item xs={12} key={post._id}>
              <PostCard post={post} onUpdate={handlePostUpdate} />
            </Grid>
          ))
        )}
      </Grid>

      {/* Load More */}
      {hasMore && posts.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <button
            onClick={handleLoadMore}
            disabled={loading}
            style={{
              padding: '12px 24px',
              backgroundColor: '#2196f3',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '16px'
            }}
          >
            {loading ? 'Loading...' : 'Load More'}
          </button>
        </Box>
      )}

      {/* Floating Action Button */}
      {isAuthenticated && (
        <Fab
          color="primary"
          aria-label="add"
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
          }}
          onClick={() => navigate('/create')}
        >
          <AddIcon />
        </Fab>
      )}
    </Container>
  );
};

export default Home;
